import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useWindows } from '../../../contexts/WindowContext';
import { useSound } from '../../../contexts/SoundContext';
import { useRouting } from '../../../contexts/RoutingContext';
import { Icon } from '../../Icons';
import { subPathToMyComputerPath, myComputerPathToSubPath } from '../../../utils/routing';
import type { AppType } from '../../../types';
import styles from '../AppContent.module.css';
import localStyles from './MyComputer.module.css';

// Virtual File System Structure
interface FileSystemNode {
  name: string;
  type: 'folder' | 'file' | 'drive' | 'app';
  icon: string;
  children?: FileSystemNode[];
  appType?: AppType;
  content?: string;
  size?: string;
}

const FILE_SYSTEM: FileSystemNode = {
  name: 'My Computer',
  type: 'folder',
  icon: 'computer',
  children: [
    {
      name: 'Local Disk (C:)',
      type: 'drive',
      icon: 'folder',
      children: [
        {
          name: 'Documents and Settings',
          type: 'folder',
          icon: 'folder',
          children: [
            {
              name: 'Guest',
              type: 'folder',
              icon: 'folder',
              children: [
                {
                  name: 'My Documents',
                  type: 'folder',
                  icon: 'folder',
                  children: [
                    { name: 'resume.pdf', type: 'file', icon: 'document', appType: 'resume', size: '245 KB' },
                    { name: 'cover_letter.doc', type: 'file', icon: 'document', size: '32 KB' },
                  ],
                },
                {
                  name: 'Desktop',
                  type: 'folder',
                  icon: 'folder',
                  children: [
                    { name: 'My Computer.lnk', type: 'file', icon: 'computer', appType: 'my-computer', size: '1 KB' },
                    { name: 'Recycle Bin.lnk', type: 'file', icon: 'recycle-bin', appType: 'recycle-bin', size: '1 KB' },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: 'Program Files',
          type: 'folder',
          icon: 'folder',
          children: [
            {
              name: 'Internet Explorer',
              type: 'folder',
              icon: 'folder',
              children: [
                { name: 'iexplore.exe', type: 'app', icon: 'internet-explorer', appType: 'internet-explorer', size: '89 KB' },
              ],
            },
            {
              name: 'MSN Messenger',
              type: 'folder',
              icon: 'folder',
              children: [
                { name: 'msnmsgr.exe', type: 'app', icon: 'msn-messenger', appType: 'msn-messenger', size: '156 KB' },
              ],
            },
            {
              name: 'Accessories',
              type: 'folder',
              icon: 'folder',
              children: [
                { name: 'notepad.exe', type: 'app', icon: 'notepad', appType: 'notepad', size: '45 KB' },
                { name: 'cmd.exe', type: 'app', icon: 'command-prompt', appType: 'command-prompt', size: '387 KB' },
              ],
            },
            {
              name: 'Games',
              type: 'folder',
              icon: 'folder',
              children: [
                { name: 'Minesweeper.exe', type: 'app', icon: 'minesweeper', appType: 'minesweeper', size: '112 KB' },
                { name: 'Solitaire.exe', type: 'app', icon: 'solitaire', appType: 'solitaire', size: '143 KB' },
              ],
            },
          ],
        },
        {
          name: 'Windows',
          type: 'folder',
          icon: 'folder',
          children: [
            { name: 'System32', type: 'folder', icon: 'folder', children: [] },
            { name: 'Fonts', type: 'folder', icon: 'folder', children: [] },
            { name: 'explorer.exe', type: 'file', icon: 'computer', size: '1,032 KB' },
          ],
        },
      ],
    },
    {
      name: 'My Projects',
      type: 'folder',
      icon: 'folder-projects',
      appType: 'projects',
      children: [
        { name: 'WattNest', type: 'folder', icon: 'folder', children: [] },
        { name: 'XP Portfolio', type: 'folder', icon: 'folder', children: [] },
        { name: 'Algorithm Visualizer', type: 'folder', icon: 'folder', children: [] },
      ],
    },
    {
      name: 'About Me',
      type: 'folder',
      icon: 'user',
      appType: 'about-me',
    },
    {
      name: 'Contact',
      type: 'folder',
      icon: 'mail',
      appType: 'contact',
    },
    {
      name: 'Blog',
      type: 'folder',
      icon: 'notepad',
      appType: 'blog',
    },
    {
      name: 'Control Panel',
      type: 'folder',
      icon: 'settings',
      appType: 'settings',
    },
  ],
};

// Helper to find node at path
const findNodeAtPath = (path: string[]): FileSystemNode | null => {
  if (path.length === 0) return FILE_SYSTEM;

  let current: FileSystemNode | undefined = FILE_SYSTEM;
  for (const segment of path) {
    if (!current.children) return null;
    current = current.children.find(c => c.name === segment);
    if (!current) return null;
  }
  return current;
};

export const MyComputer: React.FC = () => {
  const { openWindow } = useWindows();
  const { playSound } = useSound();
  const { getInitialSubPath, setSubPath } = useRouting();
  const initializedRef = useRef(false);

  // Initialize from URL sub-path on mount
  const [currentPath, setCurrentPath] = useState<string[]>(() => {
    const subPathVal = getInitialSubPath('my-computer');
    return subPathToMyComputerPath(subPathVal);
  });
  const [history, setHistory] = useState<string[][]>(() => {
    const subPathVal = getInitialSubPath('my-computer');
    const initial = subPathToMyComputerPath(subPathVal);
    return [initial];
  });
  const [historyIndex, setHistoryIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  // Sync URL when path changes (skip initial mount)
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    setSubPath('my-computer', myComputerPathToSubPath(currentPath));
  }, [currentPath, setSubPath]);

  const currentNode = findNodeAtPath(currentPath) || FILE_SYSTEM;
  const items = currentNode.children || [];

  const navigateTo = useCallback((path: string[]) => {
    setCurrentPath(path);
    setSelectedItem(null);
    // Add to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(path);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    playSound('click');
  }, [history, historyIndex, playSound]);

  const handleBack = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setCurrentPath(history[historyIndex - 1]);
      setSelectedItem(null);
      playSound('click');
    }
  }, [historyIndex, history, playSound]);

  const handleForward = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setCurrentPath(history[historyIndex + 1]);
      setSelectedItem(null);
      playSound('click');
    }
  }, [historyIndex, history, playSound]);

  const handleUp = useCallback(() => {
    if (currentPath.length > 0) {
      navigateTo(currentPath.slice(0, -1));
    }
  }, [currentPath, navigateTo]);

  const handleItemClick = useCallback((item: FileSystemNode) => {
    setSelectedItem(item.name);
    playSound('click');
  }, [playSound]);

  const handleItemDoubleClick = useCallback((item: FileSystemNode) => {
    if (item.appType) {
      openWindow(item.appType);
      playSound('click');
    } else if (item.type === 'folder' || item.type === 'drive') {
      navigateTo([...currentPath, item.name]);
    }
  }, [currentPath, navigateTo, openWindow, playSound]);

  const handleBreadcrumbClick = useCallback((index: number) => {
    if (index === -1) {
      navigateTo([]);
    } else {
      navigateTo(currentPath.slice(0, index + 1));
    }
  }, [currentPath, navigateTo]);

  // Get folder/file counts
  const folderCount = items.filter(i => i.type === 'folder' || i.type === 'drive').length;
  const fileCount = items.filter(i => i.type === 'file' || i.type === 'app').length;

  return (
    <div className={styles.appContainer}>
      <div className={styles.toolbar}>
        <button
          className={styles.toolbarButton}
          onClick={handleBack}
          disabled={historyIndex === 0}
          title="Back"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M10 2 L4 8 L10 14 L10 10 L14 10 L14 6 L10 6 Z"/>
          </svg>
          Back
        </button>
        <button
          className={styles.toolbarButton}
          onClick={handleForward}
          disabled={historyIndex >= history.length - 1}
          title="Forward"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M6 2 L12 8 L6 14 L6 10 L2 10 L2 6 L6 6 Z"/>
          </svg>
          Forward
        </button>
        <button
          className={styles.toolbarButton}
          onClick={handleUp}
          disabled={currentPath.length === 0}
          title="Up"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 2 L2 10 L5 10 L5 14 L11 14 L11 10 L14 10 Z"/>
          </svg>
          Up
        </button>
        <div style={{ width: 1, height: 20, background: '#ACA899', margin: '0 4px' }} />
        <button className={styles.toolbarButton}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 2 L8 4 L2 4 L2 14 L14 14 L14 4 L8 4 L6 2 Z"/>
          </svg>
          Folders
        </button>
      </div>

      <div className={styles.addressBar}>
        <span className={styles.addressLabel}>Address</span>
        <div className={localStyles.breadcrumb}>
          <button
            className={localStyles.breadcrumbItem}
            onClick={() => handleBreadcrumbClick(-1)}
          >
            <Icon name="computer" size={16} />
            <span>My Computer</span>
          </button>
          {currentPath.map((segment, index) => (
            <React.Fragment key={index}>
              <span className={localStyles.breadcrumbSeparator}>&gt;</span>
              <button
                className={localStyles.breadcrumbItem}
                onClick={() => handleBreadcrumbClick(index)}
              >
                {segment}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className={localStyles.content}>
        <div className={localStyles.sidebar}>
          <div className={localStyles.sidebarSection}>
            <div className={localStyles.sidebarHeader}>
              <span>System Tasks</span>
            </div>
            <div className={localStyles.sidebarItem}>View system information</div>
            <div className={localStyles.sidebarItem}>Add or remove programs</div>
            <div className={localStyles.sidebarItem}>Change a setting</div>
          </div>
          <div className={localStyles.sidebarSection}>
            <div className={localStyles.sidebarHeader}>
              <span>Other Places</span>
            </div>
            <div
              className={localStyles.sidebarItem}
              onClick={() => openWindow('projects')}
            >
              My Projects
            </div>
            <div
              className={localStyles.sidebarItem}
              onClick={() => navigateTo([])}
            >
              My Computer
            </div>
            <div
              className={localStyles.sidebarItem}
              onClick={() => openWindow('settings')}
            >
              Control Panel
            </div>
          </div>
          <div className={localStyles.sidebarSection}>
            <div className={localStyles.sidebarHeader}>
              <span>Details</span>
            </div>
            {selectedItem ? (
              <div className={localStyles.detailsPane}>
                <div className={localStyles.detailItem}>{selectedItem}</div>
              </div>
            ) : (
              <div className={localStyles.detailsPane}>
                <div className={localStyles.detailItem}>
                  {folderCount > 0 && `${folderCount} folder${folderCount !== 1 ? 's' : ''}`}
                  {folderCount > 0 && fileCount > 0 && ', '}
                  {fileCount > 0 && `${fileCount} file${fileCount !== 1 ? 's' : ''}`}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={localStyles.main}>
          <div className={localStyles.sectionHeader}>
            <Icon name={currentNode.icon || 'folder'} size={20} />
            <span>{currentNode.name}</span>
          </div>
          {items.length > 0 ? (
            <div className={localStyles.itemsGrid}>
              {items.map((item) => (
                <div
                  key={item.name}
                  className={`${localStyles.item} ${selectedItem === item.name ? localStyles.itemSelected : ''}`}
                  onClick={() => handleItemClick(item)}
                  onDoubleClick={() => handleItemDoubleClick(item)}
                  tabIndex={0}
                  role="button"
                  aria-label={item.name}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleItemDoubleClick(item);
                  }}
                >
                  <Icon name={item.icon} size={48} />
                  <div className={localStyles.itemName}>{item.name}</div>
                  {item.size && <div className={localStyles.itemType}>{item.size}</div>}
                  {!item.size && item.type === 'folder' && (
                    <div className={localStyles.itemType}>File Folder</div>
                  )}
                  {!item.size && item.type === 'drive' && (
                    <div className={localStyles.itemType}>Local Disk</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={localStyles.emptyFolder}>
              <p>This folder is empty.</p>
            </div>
          )}
        </div>
      </div>

      <div className={styles.statusBar}>
        {items.length} object{items.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default MyComputer;
